import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc, updateDoc, getDocs } from 'firebase/firestore';
import { List, ListItemText, Button, TextField, Box, Typography, Divider, ListItemButton, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Avatar, useMediaQuery, useTheme } from '@mui/material';
import { Chat as ChatIcon, Add as AddIcon, Search as SearchIcon, MoreVert as MoreIcon, Download as DownloadIcon, Clear as ClearIcon, Delete as DeleteIcon, Edit as EditIcon, MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createThread, categorizeConversation } from '../services/openai';

interface Conversation {
  id: string;
  title: string;
  createdAt: Date;
  userId: string;
  lastMessage?: string;
  updatedAt?: Date;
  threadId?: string;
  hashtags?: string[];
}

interface ConversationListProps {
  onSelectConversation: (conversationId: string) => void;
  selectedConversationId: string | null;
  user: any; // User object with updated data from Firestore
}

const ConversationList: React.FC<ConversationListProps> = ({
  onSelectConversation,
  selectedConversationId,
  user
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  // Derived filtered list (avoid extra render from setState)
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // New state for editing and deleting
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [conversationMenuAnchor, setConversationMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({});

  const { t } = useTranslation();

  // Language-specific content
  const getContent = () => {
    const content = {
      en: {
        petOwnerFallback: "Pet Owner",
        freePlan: "Free",
        exportTitle: "PetSnap - Conversation Export",
        exportedOn: "Exported on",
        totalConversations: "Total Conversations",
        conversation: "Conversation",
        created: "Created",
        lastMessage: "Last Message"
      },
      'zh-CN': {
        petOwnerFallback: "宠物主人",
        freePlan: "免费版",
        exportTitle: "PetSnap - 对话导出",
        exportedOn: "导出时间",
        totalConversations: "总对话数",
        conversation: "对话",
        created: "创建时间",
        lastMessage: "最后消息"
      },
      es: {
        petOwnerFallback: "Dueño de Mascota",
        freePlan: "Gratis",
        exportTitle: "PetSnap - Exportación de Conversaciones",
        exportedOn: "Exportado el",
        totalConversations: "Total de Conversaciones",
        conversation: "Conversación",
        created: "Creado",
        lastMessage: "Último Mensaje"
      },
      fr: {
        petOwnerFallback: "Propriétaire d'Animal",
        freePlan: "Gratuit",
        exportTitle: "PetSnap - Exportation de Conversations",
        exportedOn: "Exporté le",
        totalConversations: "Total des Conversations",
        conversation: "Conversation",
        created: "Créé",
        lastMessage: "Dernier Message"
      }
    };

    const userLang = user?.language as 'en' | 'zh-CN' | 'es' | 'fr' || 'en';
    return content[userLang] || content.en;
  };

  const content = getContent();

  useEffect(() => {
    if (!user) {
      console.log('❌ ConversationList: No user provided');
      return;
    }

    console.log('🔄 ConversationList: Setting up Firestore listener for user:', user.uid);

    // Use simple query for now - index is being built
    const q = query(
      collection(db, 'conversations'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('📊 ConversationList: Firestore snapshot received:', {
        docCount: snapshot.docs.length,
        userUid: user.uid,
        docs: snapshot.docs.map(doc => ({
          id: doc.id,
          userId: doc.data().userId,
          title: doc.data().title
        }))
      });

      const conversationList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as Conversation[];

      // Sort by updatedAt descending (client-side sorting)
      conversationList.sort((a, b) => {
        const aTime = a.updatedAt || a.createdAt || new Date(0);
        const bTime = b.updatedAt || b.createdAt || new Date(0);
        return bTime.getTime() - aTime.getTime();
      });

      console.log('📋 ConversationList: Processed conversations:', conversationList);
      setConversations(conversationList);
    }, (error) => {
      console.error('❌ ConversationList: Firestore listener error:', error);
    });

    return () => unsubscribe();
  }, [user]);

  const filteredConversations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter((conversation) => {
      const inTitle = conversation.title?.toLowerCase().includes(term);
      const inLastMessage = conversation.lastMessage?.toLowerCase().includes(term);
      const inHashtags = conversation.hashtags?.some((hashtag) => hashtag.toLowerCase().includes(term));
      return Boolean(inTitle || inLastMessage || inHashtags);
    });
  }, [conversations, searchTerm]);


  const handleCreateConversation = async () => {
    if (!user) return;

    setIsCreating(true);
    try {
      // Create OpenAI thread first
      const threadId = await createThread();

      // Get localized text using translation system
      const getLocalizedText = (key: string) => {
        return t(key);
      };

      const newConversation = {
        title: getLocalizedText('newConversation'),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userId: user.uid,
        threadId: threadId,
      };

      const docRef = await addDoc(collection(db, 'conversations'), newConversation);
      onSelectConversation(docRef.id);
    } catch (error) {
      console.error('Error creating conversation:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleExportConversations = async () => {
    try {
      // Create a simple text export of all conversations
      let exportText = `${content.exportTitle}\n`;
      exportText += `${content.exportedOn}: ${new Date().toLocaleString()}\n`;
      exportText += `${content.totalConversations}: ${conversations.length}\n\n`;
      exportText += '='.repeat(50) + '\n\n';

      for (const conversation of conversations) {
        exportText += `${content.conversation}: ${conversation.title}\n`;
        exportText += `${content.created}: ${conversation.createdAt?.toLocaleString()}\n`;
        if (conversation.lastMessage) {
          exportText += `${content.lastMessage}: ${conversation.lastMessage}\n`;
        }
        exportText += '\n' + '-'.repeat(30) + '\n\n';
      }

      // Create and download the file
      const blob = new Blob([exportText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `petsnap-conversations-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting conversations:', error);
    }
    setAnchorEl(null);
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '';

    // Return date only in a readable format
    return date.toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Edit conversation title
  const handleEditTitle = (conversationId: string, currentTitle: string) => {
    setEditingConversationId(conversationId);
    setEditingTitle(currentTitle);
    setConversationMenuAnchor(prev => ({ ...prev, [conversationId]: null }));
  };

  const handleSaveTitle = async () => {
    if (!editingConversationId || !editingTitle.trim()) return;

    try {
      await updateDoc(doc(db, `conversations/${editingConversationId}`), {
        title: editingTitle.trim(),
        updatedAt: new Date(),
      });
      setEditingConversationId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Error updating conversation title:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingConversationId(null);
    setEditingTitle('');
  };

  // Delete conversation
  const handleDeleteConversation = (conversationId: string) => {
    setConversationToDelete(conversationId);
    setDeleteDialogOpen(true);
    setConversationMenuAnchor(prev => ({ ...prev, [conversationId]: null }));
  };

  const confirmDeleteConversation = async () => {
    if (!conversationToDelete) return;

    try {
      // Delete all messages in the conversation
      const messagesQuery = query(
        collection(db, `conversations/${conversationToDelete}/messages`)
      );

      // Get all messages first, then delete them
      const messagesSnapshot = await getDocs(messagesQuery);

      // Delete all messages
      const deletePromises = messagesSnapshot.docs.map((messageDoc) =>
        deleteDoc(doc(db, `conversations/${conversationToDelete}/messages/${messageDoc.id}`))
      );
      await Promise.all(deletePromises);

      // Delete the conversation document
      await deleteDoc(doc(db, `conversations/${conversationToDelete}`));

      // If this was the selected conversation, clear the selection
      if (selectedConversationId === conversationToDelete) {
        onSelectConversation('');
      }

      setDeleteDialogOpen(false);
      setConversationToDelete(null);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const cancelDeleteConversation = () => {
    setDeleteDialogOpen(false);
    setConversationToDelete(null);
  };

  // Handle conversation menu
  const handleConversationMenuOpen = (event: React.MouseEvent<HTMLElement>, conversationId: string) => {
    event.stopPropagation();
    setConversationMenuAnchor(prev => ({ ...prev, [conversationId]: event.currentTarget }));
  };

  const handleConversationMenuClose = (conversationId: string) => {
    setConversationMenuAnchor(prev => ({ ...prev, [conversationId]: null }));
  };

  // Auto-categorize conversation based on content
  const categorizeConversationContent = async (conversationId: string) => {
    try {
      // Get all messages from the conversation
      const messagesQuery = query(
        collection(db, `conversations/${conversationId}/messages`),
        orderBy('createdAt', 'asc')
      );

      const messagesSnapshot = await getDocs(messagesQuery);
      const messages = messagesSnapshot.docs.map(doc => doc.data());

      // Only categorize if there are at least 2 messages (user + assistant)
      if (messages.length >= 2) {
        const conversationContent = messages
          .map(msg => `${msg.role}: ${msg.content}`)
          .join('\n\n');

        // Get hashtags from OpenAI
        const hashtags = await categorizeConversation(conversationContent);

        // Update conversation with hashtags
        await updateDoc(doc(db, `conversations/${conversationId}`), {
          hashtags: hashtags,
          updatedAt: new Date(),
        });

        console.log('Conversation categorized with hashtags:', hashtags);
      }
    } catch (error) {
      console.error('Error categorizing conversation:', error);
    }
  };

  // Check for conversations that need categorization
  useEffect(() => {
    const checkForCategorization = async () => {
      // Find conversations without hashtags that have messages
      const uncategorizedConversations = conversations.filter(conv =>
        !conv.hashtags && conv.lastMessage
      );

      // Categorize up to 3 conversations at a time to avoid rate limits
      const conversationsToProcess = uncategorizedConversations.slice(0, 3);

      for (const conversation of conversationsToProcess) {
        await categorizeConversationContent(conversation.id);
        // Add delay between requests to respect rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    };

    if (conversations.length > 0) {
      checkForCategorization();
    }
  }, [conversations]);

  return (
    <Box sx={{
      height: '100%',
      maxHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      flexShrink: 1,
      overflow: 'hidden',
      position: 'relative',
      bgcolor: 'var(--petsnap-surface)',
      borderRight: '1px solid rgba(38, 70, 83, 0.06)'
    }}>
      {/* Header */}
      <Box sx={{ p: isMobile ? 2 : 2.5, borderBottom: '1px solid rgba(38, 70, 83, 0.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: 'var(--petsnap-forest)', fontFamily: "'Outfit', sans-serif" }}>
            {t('chatHistory')}
          </Typography>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="small"
            sx={{ color: 'var(--text-secondary)' }}
          >
            <MoreIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: { bgcolor: 'var(--petsnap-surface-elevated)', border: '1px solid rgba(38, 70, 83, 0.08)', boxShadow: '0 4px 20px rgba(38, 70, 83, 0.1)' }
            }}
          >
            <MenuItem onClick={handleExportConversations} sx={{ color: 'var(--petsnap-forest)' }}>
              <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
              {t('exportConversations')}
            </MenuItem>
          </Menu>
        </Box>

        {/* Search Bar */}
        <Box sx={{ position: 'relative', mb: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('searchConversationsOrHashtags')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'var(--text-muted)', mr: 1, fontSize: 20 }} />,
              endAdornment: searchTerm && (
                <IconButton size="small" onClick={clearSearch} sx={{ color: 'var(--text-muted)' }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                fontSize: isMobile ? '16px' : '14px',
                bgcolor: '#F8F9FA',
                color: 'var(--text-primary)',
                '& fieldset': { borderColor: 'rgba(38, 70, 83, 0.08)' },
                '&:hover fieldset': { borderColor: 'var(--petsnap-honey)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--petsnap-honey)', borderWidth: 1 }
              },
            }}
          />
        </Box>

        <Button
          variant="contained"
          fullWidth
          startIcon={<AddIcon />}
          onClick={handleCreateConversation}
          disabled={isCreating}
          sx={{
            py: 1.2,
            borderRadius: 3,
            background: 'var(--gradient-golden)',
            boxShadow: '0 4px 12px rgba(244, 162, 97, 0.2)',
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '0.95rem',
            fontFamily: "'Outfit', sans-serif",
            '&:hover': {
              background: 'linear-gradient(135deg, #F4A261 0%, #E76F51 100%)',
              filter: 'brightness(1.05)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 16px rgba(244, 162, 97, 0.3)',
            }
          }}
        >
          {isCreating ? t('creating') : t('newChat')}
        </Button>
      </Box>

      {/* Conversation List */}
      <Box
        className="petsnap-scrollbar"
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 0 // Important for nested flex scroll
        }}
      >
        {filteredConversations.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', opacity: 0.5 }}>
            {searchTerm ? (
              <>
                <SearchIcon sx={{ fontSize: 40, color: 'var(--text-muted)', mb: 2 }} />
                <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                  {t('noConversationsFoundMatching', { searchTerm })}
                </Typography>
              </>
            ) : (
              <>
                <ChatIcon sx={{ fontSize: 40, color: 'var(--text-muted)', mb: 2 }} />
                <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                  {t('noConversationsYet')}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <List sx={{ p: 1 }}>
            {filteredConversations.map((conversation, index) => (
              <React.Fragment key={conversation.id}>
                <ListItemButton
                  onClick={() => editingConversationId !== conversation.id && onSelectConversation(conversation.id)}
                  selected={selectedConversationId === conversation.id}
                  sx={{
                    py: 1.5,
                    px: 1.5,
                    borderRadius: 3,
                    mb: 0.5,
                    border: '1px solid transparent',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&.Mui-selected': {
                      bgcolor: '#FFF8F0', // Very light honey/cream
                      borderColor: 'rgba(244, 162, 97, 0.3)',
                      '&:hover': { bgcolor: '#FFF8F0' },
                      '& .MuiTypography-subtitle2': { color: 'var(--petsnap-honey)' }
                    },
                    '&:hover': {
                      bgcolor: 'rgba(38, 70, 83, 0.03)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      editingConversationId === conversation.id ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveTitle();
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                            autoFocus
                            sx={{
                              flexGrow: 1,
                              '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,0.2)', color: 'white' }
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{
                            fontWeight: selectedConversationId === conversation.id ? 700 : 500,
                            color: selectedConversationId === conversation.id ? 'var(--petsnap-honey)' : 'var(--text-primary)',
                            fontSize: '0.95rem',
                            fontFamily: selectedConversationId === conversation.id ? "'Outfit', sans-serif" : "'Inter', sans-serif"
                          }}
                        >
                          {conversation.title}
                        </Typography>
                      )
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      editingConversationId !== conversation.id && (
                        <Box>
                          {conversation.lastMessage && (
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{
                                display: 'block',
                                color: 'var(--text-muted)',
                                mb: 0.5,
                                fontSize: '0.75rem'
                              }}
                            >
                              {conversation.lastMessage}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                              {formatDate(conversation.updatedAt || conversation.createdAt)}
                            </Typography>
                            {conversation.hashtags?.slice(0, 2).map((hashtag, idx) => (
                              <Typography key={idx} variant="caption" sx={{ color: 'var(--petsnap-honey)', fontWeight: 600, fontSize: '0.7rem', bgcolor: '#FFF8F0', px: 0.8, py: 0.2, borderRadius: 1 }}>
                                #{hashtag}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )
                    }
                  />

                  {editingConversationId !== conversation.id && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleConversationMenuOpen(e, conversation.id)}
                      sx={{ color: 'var(--text-muted)', opacity: selectedConversationId === conversation.id ? 1 : 0, transition: 'opacity 0.2s' }}
                    >
                      <MoreHorizIcon fontSize="small" />
                    </IconButton>
                  )}

                  <Menu
                    anchorEl={conversationMenuAnchor[conversation.id]}
                    open={Boolean(conversationMenuAnchor[conversation.id])}
                    onClose={() => handleConversationMenuClose(conversation.id)}
                    PaperProps={{
                      sx: { bgcolor: 'var(--petsnap-surface)', border: '1px solid rgba(38, 70, 83, 0.08)', boxShadow: '0 4px 20px rgba(38, 70, 83, 0.1)' }
                    }}
                  >
                    <MenuItem onClick={() => handleEditTitle(conversation.id, conversation.title)} sx={{ color: 'var(--text-primary)' }}>
                      <EditIcon sx={{ mr: 1, fontSize: 18 }} />
                      {t('editTitle')}
                    </MenuItem>
                    <MenuItem onClick={() => handleDeleteConversation(conversation.id)} sx={{ color: 'var(--petsnap-terracotta)' }}>
                      <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
                      {t('delete')}
                    </MenuItem>
                  </Menu>
                </ListItemButton>
                {index < filteredConversations.length - 1 && <Divider sx={{ opacity: 0.05 }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* User Profile Section */}
      <Box sx={{
        p: 2,
        borderTop: '1px solid rgba(38, 70, 83, 0.08)',
        bgcolor: 'var(--petsnap-surface)',
        flexShrink: 0
      }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            p: 1,
            borderRadius: 3,
            transition: 'background 0.2s',
            '&:hover': { bgcolor: 'rgba(38, 70, 83, 0.04)' }
          }}
          onClick={() => {
            const event = new CustomEvent('openUserProfile');
            window.dispatchEvent(event);
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'var(--petsnap-honey)',
              border: '2px solid white',
              boxShadow: '0 2px 8px rgba(244, 162, 97, 0.25)',
              fontWeight: 700
            }}
            src={user?.photoURL}
          >
            {!user?.photoURL && user?.displayName?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 700, color: 'var(--petsnap-forest)', lineHeight: 1.2, fontFamily: "'Outfit', sans-serif" }}>
              {user?.displayName || content.petOwnerFallback}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--petsnap-honey)', fontWeight: 600, fontSize: '0.7rem' }}>
              {content.freePlan.toUpperCase()}
            </Typography>
          </Box>
          <IconButton size="small" sx={{ color: 'var(--text-muted)' }}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteConversation}
        PaperProps={{
          sx: {
            bgcolor: 'var(--petsnap-surface)',
            backgroundImage: 'none',
            border: '1px solid rgba(38, 70, 83, 0.08)',
            borderRadius: 4,
            boxShadow: '0 10px 40px rgba(38, 70, 83, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
          {t('deleteConversation')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-secondary)' }}>
            {t('areYouSureYouWantToDeleteThisConversation')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={cancelDeleteConversation} sx={{ color: 'var(--text-muted)' }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={confirmDeleteConversation}
            variant="contained"
            sx={{
              bgcolor: 'var(--petsnap-terracotta)',
              color: 'white',
              '&:hover': { bgcolor: '#D65A3F' }
            }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(ConversationList);